import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, useTheme, alpha, Typography, Fade, Zoom } from '@mui/material';
import { PlayArrow, Pause, Fullscreen, PlayCircle } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface VideoPlayerProps {
  videoId?: 'company' | 'dashboard' | 'monitoring';
  videoUrl?: string;
  autoPlay?: boolean;
  muted?: boolean;
  onEnded?: () => void;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoId, videoUrl, autoPlay = false, muted = true, onEnded }) => {
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const videos = {
    company: '/demo-videos/company-prez.mp4',
    dashboard: '/demo-videos/dashboard-prez.mp4',
    monitoring: '/demo-videos/monitoring-prez.mp4',
  };

  const videoTitles = {
    company: 'Gestion d\'entreprise',
    dashboard: 'Tableau de bord',
    monitoring: 'Monitoring & surveillance',
  };

  const videoDescriptions = {
    company: 'Découvrez comment organiser et gérer vos équipes efficacement',
    dashboard: 'Visualisez toutes vos données en temps réel',
    monitoring: 'Surveillez et protégez vos équipes 24/7',
  };

  const videoSrc = videoUrl || (videoId ? videos[videoId] : '');
  const videoTitle = videoId ? videoTitles[videoId] : 'Vidéo de démonstration';
  const videoDescription = videoId ? videoDescriptions[videoId] : '';

  useEffect(() => {
    if (videoRef.current) {
      if (autoPlay) {
        videoRef.current.play().catch(() => {
          // Autoplay might be blocked by browser
          setIsPlaying(false);
        });
      }
    }
  }, [autoPlay]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      videoRef.current.requestFullscreen();
    }
  };

  const handleThumbnailClick = () => {
    setShowThumbnail(false);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  };

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const handlePlay = () => {
    setIsPlaying(true);
    setShowThumbnail(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
        background: theme.palette.background.paper,
        '&:hover .video-controls': {
          opacity: 1,
        },
      }}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={videoSrc}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          objectFit: 'cover',
          imageRendering: 'crisp-edges',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
        } as React.CSSProperties}
        muted={muted}
        playsInline
        preload="auto"
        onLoadStart={handleVideoLoad}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={onEnded}
      />

      {/* Custom Thumbnail Overlay */}
      <AnimatePresence>
        {showThumbnail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg,
                  ${alpha(theme.palette.primary.main, 0.9)} 0%,
                  ${alpha(theme.palette.primary.dark, 0.8)} 50%,
                  ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: `linear-gradient(135deg,
                    ${alpha(theme.palette.primary.main, 0.95)} 0%,
                    ${alpha(theme.palette.primary.dark, 0.85)} 50%,
                    ${alpha(theme.palette.secondary.main, 0.75)} 100%)`,
                  transform: 'scale(1.02)',
                },
              }}
              onClick={handleThumbnailClick}
            >
              {/* Background Pattern */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%),
                                   radial-gradient(circle at 80% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)`,
                  opacity: 0.6,
                }}
              />

              {/* Content */}
              <Box sx={{ textAlign: 'center', zIndex: 2, position: 'relative' }}>
                {/* Play Button */}
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.common.white, 0.9)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
                      border: `3px solid ${alpha(theme.palette.common.white, 0.3)}`,
                    }}
                  >
                    <PlayArrow
                      sx={{
                        fontSize: 40,
                        color: theme.palette.primary.main,
                        marginLeft: '4px' // Centrer visuellement l'icône
                      }}
                    />
                  </Box>
                </motion.div>

                {/* Title */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      mb: 2,
                      textShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.3)}`,
                    }}
                  >
                    {videoTitle}
                  </Typography>
                </motion.div>

                {/* Description */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.9),
                      mb: 3,
                      maxWidth: 400,
                      textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.3)}`,
                    }}
                  >
                    {videoDescription}
                  </Typography>
                </motion.div>

                {/* Click to Play Text */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.8),
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontWeight: 600,
                      border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      background: alpha(theme.palette.common.white, 0.1),
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    Cliquez pour lire
                  </Typography>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Controls */}
      <Box
        className="video-controls"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.7)} 0%, transparent 100%)`,
          padding: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        <IconButton
          onClick={handlePlayPause}
          sx={{
            color: 'white',
            bgcolor: alpha(theme.palette.common.white, 0.2),
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.3),
            },
          }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <IconButton
          onClick={handleFullscreen}
          sx={{
            color: 'white',
            bgcolor: alpha(theme.palette.common.white, 0.2),
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.3),
            },
          }}
        >
          <Fullscreen />
        </IconButton>
      </Box>
    </Box>
  );
};
